import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/PasswordInput';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Default to root so RootRedirect honors the user's home_screen preference.
  // If they were bounced here from a protected route, return them there.
  const from = location.state?.from?.pathname || '/';

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const { error } = await signIn(email.trim(), password);
    setSubmitting(false);
    if (error) {
      toast.error(error.message || 'Unable to sign in');
      return;
    }
    toast.success('Welcome back');
    navigate(from, { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col relative z-10" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-primary mb-8 flex justify-center"><Logo /></div>
        <div className="rounded-2xl border border-border bg-card p-7 sm:p-9">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1.5">Sign in to your flow.</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" autoComplete="email" />
            </div>
            <div>
              <div className="flex justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground" data-testid="login-forgot-link">Forgot?</Link>
              </div>
              <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} testid="login-password" autoComplete="current-password" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full rounded-full mt-2" data-testid="login-submit">
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Sign in
            </Button>
          </form>
          <p className="mt-6 text-sm text-center text-muted-foreground">
            New here? <Link to="/signup" className="text-foreground font-medium hover:underline" data-testid="login-signup-link">Create an account</Link>
          </p>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
}
